import React from 'react';

import { FlowContextProvider, StepWrapper, when } from '@betterment/js-runtime';
import { Route, Routes, useMatch } from 'react-router-dom';

import { AssetBasedBillingConfigurationStep } from './asset-based-billing/AssetBasedBillingConfigurationStep';
import { ConfigurationSelectionStep } from './ConfigurationSelectionStep';
import { BillingPlanFlowValues, isExistingPlanWithClients } from './data';
import { FixedBillingConfigurationStep } from './fixed-billing/FixedBillingConfigurationStep';
import { ReviewAffectedHouseholds } from './review-affected-households/ReviewAffectedHouseholds';
import { TieredBillingConfigurationStep } from './tiered-billing/TieredBillingConfigurationStep';

import { IdempotencyProviderWithFeatureRegistry } from '../../../providers/IdempotencyProviderWithFeatureRegistry';
import FlowLayout from '../../BettermentForAdvisors/FlowLayout';

const stepMap = [
  {
    name: 'configuration_type_selection',
    element: ConfigurationSelectionStep,
    next: [
      [
        'asset_based_configuration',
        when<BillingPlanFlowValues>((_, { values }) => values.configurationType === 'BillingRateAssetBased'),
      ],
      [
        'fixed_based_configuration',
        when<BillingPlanFlowValues>((_, { values }) => values.configurationType === 'BillingRateFixed'),
      ],
      [
        'tiered_configuration',
        when<BillingPlanFlowValues>((_, { values }) => values.configurationType === 'BillingRateTiered'),
      ],
    ],
  },
  {
    name: 'asset_based_configuration',
    element: AssetBasedBillingConfigurationStep,
    previous: 'configuration_type_selection',
    next: [['review_affected_households', when<BillingPlanFlowValues>(values => isExistingPlanWithClients(values))]],
  },
  {
    name: 'fixed_based_configuration',
    element: FixedBillingConfigurationStep,
    previous: 'configuration_type_selection',
    next: [['review_affected_households', when<BillingPlanFlowValues>(values => isExistingPlanWithClients(values))]],
  },
  {
    name: 'tiered_configuration',
    element: TieredBillingConfigurationStep,
    previous: 'configuration_type_selection',
    next: [['review_affected_households', when<BillingPlanFlowValues>(values => isExistingPlanWithClients(values))]],
  },
  {
    name: 'review_affected_households',
    element: ReviewAffectedHouseholds,
    previous: [
      [
        'asset_based_configuration',
        when<BillingPlanFlowValues>(values => values.configurationType === 'BillingRateAssetBased'),
      ],
      [
        'fixed_based_configuration',
        when<BillingPlanFlowValues>(values => values.configurationType === 'BillingRateFixed'),
      ],
      ['tiered_configuration', when<BillingPlanFlowValues>(values => values.configurationType === 'BillingRateTiered')],
    ],
  },
];

const FlowController: React.FC<{ url: string; initialValues: BillingPlanFlowValues }> = ({ url, initialValues }) => {
  const exitToPath = initialValues.exitToPath || '/client_billing/billing_plans';
  const returnToPath = exitToPath;
  const match = useMatch(url);

  return (
    <FlowLayout>
      <IdempotencyProviderWithFeatureRegistry featureName="billingPlans">
        {/* @ts-expect-error TS18047 violates strict null checks */}
        <FlowContextProvider basePath={match.pathnameBase} initialValues={initialValues} returnToPath={returnToPath}>
          <Routes>
            {stepMap.map(step => {
              const StepComponent = step.element;
              return (
                <Route
                  path={`/${step.name}`}
                  key={step.name}
                  element={
                    <StepWrapper name={step.name} next={step.next} previous={step.previous}>
                      {stepProps => <StepComponent exitToPath={exitToPath} {...stepProps} />}
                    </StepWrapper>
                  }
                />
              );
            })}
          </Routes>
        </FlowContextProvider>
      </IdempotencyProviderWithFeatureRegistry>
    </FlowLayout>
  );
};

export default FlowController;
